import React, { useEffect, useRef, useState } from 'react';
import { api } from '../../api/client';
import './HomeVisitCounter.css';

const LS_KEY = 'luxistt_home_visit_registered_day';

function localDayString() {
  try {
    return new Date().toLocaleDateString('en-CA', { timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone });
  } catch {
    return new Date().toISOString().slice(0, 10);
  }
}

function easeOutCubic(t) {
  return 1 - (1 - t) ** 3;
}

/**
 * Fixed “visitors today” ribbon (center-left). Registers one hit per browser per calendar day (local dedupe + API).
 */
export default function HomeVisitCounter() {
  const [target, setTarget] = useState(null);
  const [display, setDisplay] = useState(0);
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(false);
  const animRef = useRef(null);
  const displayRef = useRef(0);

  useEffect(() => {
    displayRef.current = display;
  }, [display]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stats = await api.get('/public/home-visit-stats', null);
        if (cancelled) return;
        const serverDay = typeof stats?.day === 'string' ? stats.day : localDayString();
        const registered = typeof localStorage !== 'undefined' ? localStorage.getItem(LS_KEY) : null;
        let next = Number(stats?.today_count);
        if (registered !== serverDay) {
          const reg = await api.post('/public/home-visit-stats', {}, null);
          if (cancelled) return;
          if (typeof localStorage !== 'undefined') {
            localStorage.setItem(LS_KEY, serverDay);
          }
          next = Number(reg?.today_count);
        }
        if (cancelled) return;
        setTarget(Number.isFinite(next) ? next : 0);
      } catch {
        if (!cancelled) {
          setHidden(true);
        }
      } finally {
        if (!cancelled) {
          setReady(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (target == null) return undefined;
    const prefersReduce =
      typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduce) {
      setDisplay(target);
      return undefined;
    }
    const start = displayRef.current;
    const delta = target - start;
    if (delta === 0) return undefined;
    const duration = Math.min(900, 320 + Math.abs(delta) * 12);
    const t0 = performance.now();
    if (animRef.current) {
      cancelAnimationFrame(animRef.current);
    }
    const step = (now) => {
      const elapsed = now - t0;
      const u = Math.min(1, elapsed / duration);
      const v = Math.round(start + delta * easeOutCubic(u));
      setDisplay(v);
      if (u < 1) {
        animRef.current = requestAnimationFrame(step);
      }
    };
    animRef.current = requestAnimationFrame(step);
    return () => {
      if (animRef.current) {
        cancelAnimationFrame(animRef.current);
      }
    };
  }, [target]);

  if (hidden || !ready) {
    return null;
  }

  const formatted = new Intl.NumberFormat('en-NG').format(display);

  return (
    <aside className="home-visit-counter" aria-label="Estimated site visitors today">
      <div className="home-visit-counter__sheen" aria-hidden />
      <div className="home-visit-counter__frame">
        <span className="home-visit-counter__eyebrow">Today</span>
        <span className="home-visit-counter__value" aria-live="polite">
          {formatted}
        </span>
        <span className="home-visit-counter__caption">visitors</span>
      </div>
    </aside>
  );
}
